import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { StepperButtons } from "../buttons";
import { StepperSidebar } from "../sidebar";
import { Input } from "@/components/ui/input";
import { capitalizeFirstLetter } from "@revelate/utils";
import DatePicker from "@/currencies/components/date-picker";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Form } from "@/components/ui/form";
import { Field, FieldContent, FieldGroup, FieldLabel, FieldLegend, FieldSet, FieldTitle } from "@/components/ui/field";
import { useStepper } from "@/components/ui/stepper";

const planBuilderSchema = z.object({
  name: z
    .string()
    .min(2, {
      message: "Name must be at least 2 characters.",
    })
    .max(40, {
      message: "Name must not be longer than 40 characters.",
    }),
  time_period: z.string({
    required_error: "Please select a time period.",
  }),
  start_date: z.date().optional(),
  end_date: z.date().optional(),
});

type PlanBuilderForm = z.infer<typeof planBuilderSchema>;


export function PlanBasic() {
  const { onNext } = useStepper();

  const defaultValues: Partial<PlanBuilderForm> = {
    name: "",
    time_period: "month",
    start_date: undefined,
    end_date: undefined,
  };

  const form = useForm<PlanBuilderForm>({
    resolver: zodResolver(planBuilderSchema),
    defaultValues,
    mode: "onChange",
  });

  async function handleNext() {
    const isValid = await form.trigger();
    if (isValid) onNext();
  }

  return (
    <div className="flex flex-row gap-x-10 justify-stretch">
      {/* Form */}
      <div className="flex flex-col justify-between gap-y-10 flex-1 min-h-[300px]">
        <Form {...form}>
          <form onSubmit={() => { }} className="flex flex-col gap-y-6">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={`${capitalizeFirstLetter(
                        form.watch("time_period")
                      )}ly Sales Bonus`}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex flex-row">
              <FormField
                control={form.control}
                name="start_date"
                render={({ field: { value, onChange } }) => (
                  <FormItem>
                    <FormLabel>Start date</FormLabel>
                    <FormControl>
                      <DatePicker
                        date={value}
                        setDate={onChange}
                        className="[&>button]:w-[260px]"
                        placeholder="No start date"
                      />
                    </FormControl>
                    <FormDescription className="text-xs">
                      Optionally limit the start and end dates for the plan
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="end_date"
                render={({ field: { value, onChange } }) => (
                  <FormItem>
                    <FormLabel>End date</FormLabel>
                    <FormControl>
                      <DatePicker
                        date={value}
                        setDate={onChange}
                        className="[&>button]:w-[260px]"
                        placeholder="No end date"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>


            <FormField
              control={form.control}
              name="time_period"
              render={({ field }) => (
                <FieldGroup className="w-full max-w-xs">
                  <FieldSet>
                    <FieldLegend variant="label">
                      Payment Frequency
                    </FieldLegend>
                    <RadioGroup
                      defaultValue={field.value}
                      className="flex flex-row gap-x-2"
                      onValueChange={field.onChange}
                    >
                      <FieldLabel htmlFor="month">
                        <Field orientation="horizontal">
                          <FieldContent className="w-24">
                            <FieldTitle>Monthly</FieldTitle>
                            {/* <FieldDescription> */}
                            {/*   Every month */}
                            {/* </FieldDescription> */}
                          </FieldContent>
                          <RadioGroupItem value="month" id="month" className="invisible" />
                        </Field>
                      </FieldLabel>
                      <FieldLabel htmlFor="quarter">
                        <Field orientation="horizontal">
                          <FieldContent className="w-24">
                            <FieldTitle>Quarterly</FieldTitle>
                            {/* <FieldDescription> */}
                            {/*   Mar, Jun, Sep, Dec */}
                            {/* </FieldDescription> */}
                          </FieldContent>
                          <RadioGroupItem value="quarter" id="quarter" className="invisible" />
                        </Field>
                      </FieldLabel>
                      <FieldLabel htmlFor="year">
                        <Field orientation="horizontal">
                          <FieldContent className="w-24">
                            <FieldTitle>Yearly</FieldTitle>
                            {/* <FieldDescription> */}
                            {/*   Dec */}
                            {/* </FieldDescription> */}
                          </FieldContent>
                          <RadioGroupItem value="year" id="year" className="invisible" />
                        </Field>
                      </FieldLabel>
                    </RadioGroup>
                  </FieldSet>
                </FieldGroup>
              )}
            />
          </form>
        </Form>
        <StepperButtons onNext={handleNext} />
      </div>
      <StepperSidebar />
    </div>
  );
}
